import { useId, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";
import { extractAxiosDetail, fullName } from "./format";
import { useAssignStoreOwner, useDebouncedSearch, useLocationAdminSearch, useUnassignedStores } from "./hooks";
import type { LocationAdmin, UnassignedStore } from "./types";

/** Debounced "search an admin" combobox; calls `onPick` with the chosen Location Manager. */
function AdminTypeahead({
  store,
  disabled,
  onPick
}: {
  store: UnassignedStore;
  disabled?: boolean;
  onPick: (admin: LocationAdmin) => void;
}) {
  const inputId = useId();
  const listId = useId();
  const search = useDebouncedSearch(250);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const results = useLocationAdminSearch(search.query, { enabled: open });
  const options = search.query ? (results.data?.items ?? []) : [];
  const showList = open && search.input.trim() !== "";

  const pick = (admin: LocationAdmin) => {
    setOpen(false);
    setActiveIndex(-1);
    search.setInput("");
    onPick(admin);
  };

  return (
    <div className="directory-typeahead">
      <label htmlFor={inputId} className="visually-hidden">
        Assign an admin to {store.business_name}
      </label>
      <input
        id={inputId}
        type="text"
        role="combobox"
        className="directory-search directory-typeahead-input"
        placeholder="Assign admin — search name or email"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        value={search.input}
        disabled={disabled}
        onChange={(e) => {
          search.setInput(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => (options.length ? (i + 1) % options.length : -1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
          } else if (e.key === "Enter") {
            if (showList && activeIndex >= 0 && options[activeIndex]) {
              e.preventDefault();
              pick(options[activeIndex]);
            }
          } else if (e.key === "Escape") {
            if (showList) {
              e.stopPropagation();
              setOpen(false);
            }
          }
        }}
      />
      {showList ? (
        <ul id={listId} role="listbox" className="directory-typeahead-list" aria-label="Matching location admins">
          {search.input.trim() !== search.query || results.isFetching ? (
            <li className="directory-typeahead-status" role="presentation">
              Searching…
            </li>
          ) : results.isError ? (
            <li className="directory-typeahead-status directory-sync-bar-error" role="presentation">
              {extractAxiosDetail(results.error)}
            </li>
          ) : options.length === 0 ? (
            <li className="directory-typeahead-status" role="presentation">
              No active admins match “{search.query}”.
            </li>
          ) : (
            options.map((admin, i) => (
              <li
                key={admin.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                data-active={i === activeIndex || undefined}
                className="directory-typeahead-option"
                // mousedown (not click) so the input's blur doesn't close the list first
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(admin);
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="directory-person-name">{fullName(admin.first_name, admin.last_name, admin.email)}</span>
                <span className="directory-person-email">{admin.email}</span>
                <span className="directory-muted-small">
                  {admin.store_count} location{admin.store_count === 1 ? "" : "s"}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** Inline panel listing active locations with no Location Manager, with per-row assignment. */
export default function UnassignedStoresPanel({
  id,
  onNotice
}: {
  id?: string;
  onNotice: (text: string) => void;
}) {
  const query = useUnassignedStores();
  const assign = useAssignStoreOwner();
  const [pending, setPending] = useState<{ store: UnassignedStore; admin: LocationAdmin } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const closeDialog = () => {
    setPending(null);
    setError(null);
  };

  const items = query.data?.items ?? [];
  const pendingName = pending ? fullName(pending.admin.first_name, pending.admin.last_name, pending.admin.email) : "";

  return (
    <section id={id} className="directory-panel" aria-labelledby={`${id ?? "unassigned"}-title`}>
      <div className="directory-panel-head">
        <h2 id={`${id ?? "unassigned"}-title`} className="directory-panel-title">
          Unassigned locations
        </h2>
        {query.data ? (
          <span className="directory-muted-small">
            {query.data.total} location{query.data.total === 1 ? "" : "s"} without an admin
          </span>
        ) : null}
      </div>

      {query.isLoading ? (
        <div className="directory-state" role="status">
          Loading unassigned locations…
        </div>
      ) : query.isError ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load unassigned locations.</strong> {extractAxiosDetail(query.error)}
        </div>
      ) : items.length === 0 ? (
        <p className="directory-panel-empty">Every active location has an admin.</p>
      ) : (
        <div className="directory-table-wrap directory-table-wrap--flush directory-table-wrap--popover">
          <table className="directory-table">
            <thead>
              <tr>
                <th scope="col">Location</th>
                <th scope="col">Catalog location</th>
                <th scope="col" className="directory-col-num">
                  Users
                </th>
                <th scope="col">Assign admin</th>
              </tr>
            </thead>
            <tbody>
              {items.map((store) => (
                <tr key={store.store_id}>
                  <td data-label="Location" className="directory-cell-primary">
                    <div className="directory-person">
                      <span className="directory-person-name">{store.business_name}</span>
                      <span className="directory-person-email">{store.store_id}</span>
                      {store.is_active ? null : (
                        <span className="directory-badge-row">
                          <span className="directory-badge directory-badge--inactive">Inactive</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Catalog">
                    {store.catalog_location ? (
                      <span className="directory-cell-text">{store.catalog_location}</span>
                    ) : (
                      <span className="directory-muted-italic">Not set</span>
                    )}
                  </td>
                  <td data-label="Users" className="directory-col-num">
                    <div className="directory-stack">
                      <span className="directory-count">{store.active_count}</span>
                      <span className="directory-muted-small">of {store.user_count} active</span>
                    </div>
                  </td>
                  <td data-label="Assign" className="directory-cell-assign">
                    <AdminTypeahead
                      store={store}
                      disabled={assign.isPending}
                      onPick={(admin) => {
                        setError(null);
                        setPending({ store, admin });
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={pending != null}
        title="Assign location admin"
        body={
          pending ? (
            <p>
              Assign <strong>{pending.store.business_name}</strong> ({pending.store.store_id}) to{" "}
              <strong>{pendingName}</strong> ({pending.admin.email})?
            </p>
          ) : null
        }
        confirmLabel="Assign"
        isPending={assign.isPending}
        error={error}
        onCancel={closeDialog}
        onConfirm={async () => {
          if (!pending) return;
          setError(null);
          try {
            await assign.mutateAsync({ storeId: pending.store.store_id, ownerId: pending.admin.id });
            onNotice(`${pending.store.business_name} is now managed by ${pendingName} (${pending.admin.email}).`);
            setPending(null);
          } catch (e) {
            setError(extractAxiosDetail(e));
          }
        }}
      />
    </section>
  );
}
